import * as PhotoApi from 'unsplash-js/src/methods/photos/types';
import { Component, inject } from '@angular/core';
import { first, get } from 'lodash';
import { ActivatedRoute } from '@angular/router';
import { CommonApiService } from '@services/common/common-api.service';
import { CommonComponent } from '@components/abstract/common-component.component';
import { CommonVerbsApiService } from '@services/common/common-verbs-api.service';
import { Community } from '@models/communities/community.model';
import { EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ICommunityService } from '@tokens/injection.tokens';
import { Input } from '@angular/core';
import { Media } from '@models/media/media.model';
import { Observable } from 'rxjs';
import { OnInit } from '@angular/core';
import { Output } from '@angular/core';
import { Photos } from 'unsplash-js/dist/methods/search/types/response';
import { ToastrService } from 'ngx-toastr';
import { UnsplashRequest } from '@models/collection/unsplash-request';
import { UnsplashService } from '@services/common/unsplash.service';
import { createApi } from 'unsplash-js';
import { debounceTime } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { filter } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { of } from 'rxjs';
import { switchMap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { tap } from 'rxjs';

@Component({
  selector: 'app-unsplash',
  templateUrl: './unsplash.component.html',
  providers: [UnsplashService, CommonVerbsApiService],
})
export class UnsplashComponent extends CommonComponent implements OnInit {
  communityService = inject(ICommunityService);
  community: Community = this.communityService.model ?? <Community>{};

  @Input() query = '';
  @Input() per_page = 8;
  @Output() emitter: EventEmitter<Media> = new EventEmitter();

  isEmptyList$: Observable<boolean> = of(true);
  limitControl = new FormControl(8);
  searchControl = new FormControl(this.query);

  unsplash = createApi({
    accessKey: environment.externalGallery.unsplash.token,
  });
  constructor(
    private api: CommonApiService,
    private api2: CommonVerbsApiService,
    private route: ActivatedRoute,
    private toastr: ToastrService,

    public service: UnsplashService
  ) {
    super();
  }
  ngOnInit(): void {
    this.service.setOptionValue('per_page', this.per_page);
    this.isEmptyList$ = this.service.collection$.pipe(map(x => x.total === 0));
    this.setControls();
    this.setCollection();
  }

  setControls(): void {
    const subscribe = this.searchControl.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(value => {
        this.service.setOptionValue(
          'query',
          value && value.length > 2 ? value : this.query
        );
      });
    this.unsubscribe.push(subscribe);

    const subscribe1 = this.limitControl.valueChanges
      .pipe(
        filter(x => !!x),
        map(v => ['per_page', v]),
        takeUntil(this.destroy$)
      )
      .subscribe(([option, value]) => {
        this.service.setOptionValue(`${option}`, value);
      });
    this.unsubscribe.push(subscribe1);
  }

  protected setCollection(): void {
    const subscribe = this.service.requestChange$
      .pipe(
        map(() => ({ ...this.service.options })),
        tap(() => (this.service.isLoading = true)),
        switchMap(options => this.switchMap(options)),
        takeUntil(this.destroy$)
      )
      .subscribe(http => {
        if (http && http.status === 200 && http.response) {
          this.subscribe(http.response);
        }
      });
    this.unsubscribe.push(subscribe);
    this.service.init(this.service.options);
  }

  protected switchMap(options: UnsplashRequest) {
    return this.unsplash.search.getPhotos({
      query: options.query,
      page: options.page,
      perPage: options.per_page,
    });
  }

  protected subscribe(response: Photos): void {
    this.service.isLoading = false;
    this.service.setList(response);
  }

  clear(): void {
    const options = {
      ...this.service.options,
      page: 1,
      per_page: 12,
      query: this.query,
      w: 1500,
    };
    this.service.init(options);
    this.service.clear(options);
    this.searchControl.patchValue(this.service.options.query, {
      emitEvent: false,
    });
    this.limitControl.patchValue(this.per_page, { emitEvent: false });
  }

  download(item: PhotoApi.Basic) {
    this.api2
      .post<Media[]>(`media/upload-file-from-remote`, {
        communityId: get(this.community, 'id', null),
        id: item.id,
      })
      .subscribe({
        error: err => this.toastr.error(err?.message || 'An error occurred.'),
        next: response => {
          this.emitter.emit(first(response));
        },
      });
  }
}
